import { useEffect } from "react";
import { BOARD_SIZE, TICKS_PER_SECOND, isCore, towerStats, type TowerKind } from "@empire/core-sim";
import type { WireFogView } from "@empire/shared";
import { useAttackStore } from "../../store/attack.js";
import { knownShare, knownTowers, tileAt, useIntelStore } from "../../store/intel.js";
import { summarise, usePlanStore } from "../../store/plan.js";

/**
 * What you know about the target, and what a plan would do to it (S3-2, S3-3).
 *
 * Every number on this panel is about a GUESS. The design problem is not
 * showing the guess — it is making sure nobody reads it as the truth, which is
 * why the result says "against what you know" rather than a win or a loss, and
 * why the unknown count sits next to it rather than three screens away.
 */
export function IntelPanel(): JSX.Element | null {
  const target = useAttackStore((s) => s.confirming);
  const intel = useIntelStore((s) => s.intel);
  const loading = useIntelStore((s) => s.loading);
  const error = useIntelStore((s) => s.error);
  const load = useIntelStore((s) => s.load);
  const villageId = useIntelStore((s) => s.villageId);

  useEffect(() => {
    if (target && target.villageId !== villageId) void load(target.villageId);
  }, [target, villageId, load]);

  if (!target) return null;

  return (
    <div className="border-b border-panel-edge bg-bg-raised px-3 py-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[10px] uppercase tracking-wide text-ink-dim opacity-70">
          what you know
        </p>
        {intel?.view ? (
          <p className="text-[10px] tabular-nums text-ink-dim">
            {Math.round(knownShare(intel) * 100)}% mapped
          </p>
        ) : null}
      </div>

      {loading ? <p className="mt-1 text-[11px] text-ink-dim">Looking…</p> : null}
      {error ? <p className="mt-1 text-[11px] text-warn">{error}</p> : null}

      {!loading && intel && !intel.view ? <Blind rebuilt={intel.staleBattles > 0} /> : null}

      {intel?.view ? (
        <div className="mt-2 flex gap-3">
          <MiniBoard view={intel.view} />
          <Summary />
        </div>
      ) : null}

      <DryRun />
    </div>
  );
}

/** The honest empty state, and it says which kind of empty it is. */
function Blind({ rebuilt }: { rebuilt: boolean }): JSX.Element {
  return (
    <p className="mt-1 text-[11px] text-ink-dim" data-testid="intel-blind">
      {rebuilt
        ? "They have rebuilt since you last attacked, so everything you knew is out of date. Attacking again starts the map over."
        : "You have never attacked this village. You will be going in blind — which is what the first attack is for."}
    </p>
  );
}

/**
 * A tiny top-down board. Not isometric on purpose.
 *
 * The big editor board is isometric because you build on it. This one is read
 * at a glance beside a list of units, and a 12×12 grid of dots says "how much
 * of this do I know" faster than a diamond does.
 */
function MiniBoard({ view }: { view: WireFogView }): JSX.Element {
  const cells: JSX.Element[] = [];
  for (let y = 0; y < BOARD_SIZE; y++) {
    for (let x = 0; x < BOARD_SIZE; x++) {
      const tile = tileAt(view, x, y);
      const core = isCore(x, y);
      const gate = view.gate[0] === x && view.gate[1] === y;
      cells.push(
        <span
          key={`${x},${y}`}
          data-testid={tile === null && !core ? "fog" : undefined}
          title={core ? "core" : (tile?.kind ?? "unknown")}
          className={`block h-2.5 w-2.5 rounded-[1px] ${colour(tile, core, gate)}`}
        />
      );
    }
  }
  return (
    <div
      data-testid="intel-board"
      className="grid shrink-0 gap-px"
      style={{ gridTemplateColumns: `repeat(${BOARD_SIZE}, 0.625rem)` }}
    >
      {cells}
    </div>
  );
}

function colour(tile: WireFogView["tiles"][number], core: boolean, gate: boolean): string {
  if (core) return "bg-gold/70";
  if (tile === null) return "bg-panel-edge/40"; // fog
  if (gate) return "bg-ok";
  switch (tile.kind) {
    case "road":
      return "bg-ink-dim/70";
    case "mud":
      return "bg-warn/60";
    case "barricade":
      return "bg-danger/60";
    case "tower_slot":
      return tile.tower ? "bg-gold" : "bg-ink-dim/30";
    default:
      return "bg-bg";
  }
}

function Summary(): JSX.Element {
  const intel = useIntelStore((s) => s.intel)!;
  const towers = knownTowers(intel.view);

  return (
    <div className="min-w-0 flex-1 text-[11px] text-ink-dim">
      <p data-testid="intel-unknown">
        {intel.breached ? (
          <span className="font-semibold text-ok">You have seen all of it.</span>
        ) : (
          <>
            <span className="tabular-nums text-ink">{intel.view!.unknownCount}</span> tiles of the
            defence still unknown
          </>
        )}
      </p>
      <p className="mt-0.5 tabular-nums opacity-80">
        from {intel.battles} {intel.battles === 1 ? "attack" : "attacks"}
        {intel.staleBattles > 0 ? ` · ${intel.staleBattles} out of date` : ""}
      </p>
      {towers.length > 0 ? (
        <p className="mt-1 text-ink">
          {towers
            .map((t) => `${t.count}× ${towerStats(t.kind as TowerKind, 1).name}`)
            .join(", ")}
        </p>
      ) : (
        <p className="mt-1 opacity-70">No towers seen yet.</p>
      )}
    </div>
  );
}

/**
 * Test this plan against what you know (S3-3).
 *
 * The result is always labelled partial while anything is unknown, and the
 * reason is stated rather than implied: every tile you have not seen was
 * treated as empty ground, so the real maze can only be harder than this.
 */
function DryRun(): JSX.Element | null {
  const intel = useIntelStore((s) => s.intel);
  const status = useIntelStore((s) => s.status);
  const result = useIntelStore((s) => s.result);
  const blocked = useIntelStore((s) => s.blocked);
  const run = useIntelStore((s) => s.dryRun);
  const clear = useIntelStore((s) => s.clearRun);
  const waves = usePlanStore((s) => s.waves);
  const plan = summarise(waves);

  useEffect(() => {
    clear();
  }, [waves, clear]);

  if (!intel?.view) return null;

  return (
    <div className="mt-2 border-t border-panel-edge pt-2">
      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="dry-run"
          disabled={plan.empty || status === "running"}
          onClick={run}
          className="h-11 shrink-0 rounded-lg border border-panel-edge bg-panel px-3 text-xs font-semibold text-ink-dim enabled:hover:text-ink disabled:opacity-35"
        >
          {status === "running" ? "Running…" : "Test against what you know"}
        </button>

        <p className="min-w-0 flex-1 text-[11px]" data-testid="dry-run-result">
          {status === "impossible" ? (
            <span className="text-warn">{blocked}</span>
          ) : result ? (
            <>
              <span className={result.outcome === "breach" ? "text-ok" : "text-danger"}>
                {result.outcome === "breach"
                  ? `${result.army.reached} of ${result.army.sent} would reach the core`
                  : "Your army would be stopped"}
              </span>
              <span className="text-ink-dim">
                {" "}
                in {(result.ticks / TICKS_PER_SECOND).toFixed(1)}s
              </span>
            </>
          ) : (
            <span className="text-ink-dim opacity-80">
              Free and unlimited. Uses the real simulation, on the maze you have mapped.
            </span>
          )}
        </p>
      </div>

      {result && !intel.breached ? (
        <p className="mt-1.5 text-[11px] text-warn" data-testid="dry-run-caveat">
          Partial: {intel.view.unknownCount} tiles were treated as empty ground. The real defence
          can only be harder than this, never easier.
        </p>
      ) : null}
    </div>
  );
}
